const { vestResolver } = require('@hookform/resolvers/vest')
import {
  Button,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Text,
  useModalState,
} from '@vtex/admin-ui'
import { TextArea, TextInput, useFormState } from '@vtex/admin-ui-form'
import { useNavigation } from '@vtex/raccoon-next'
import { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getObjectDiff } from 'shared'
import { Event } from 'typings'
import { IconTemplate, useFormStateContext } from 'ui'
import { useAnalytics } from 'use-analytics'
import { Promotion } from '../../../typings'
import { useTemplateMutation } from '../hooks/useTemplateMutation'
import { TemplateForm, TemplatePayload } from '../typings'
import { BudgetWarning } from './BudgetWarning'
import { EMPTY_TEMPLATE_FORM } from './constants'
import { messages } from './messages'
import { styles } from './styles'
import { validationSuite } from './validation'
import { useTrackTimer } from '../../../../hooks/useTrackTimer'

type TemplateModalProps = {
  modalState: ReturnType<typeof useModalState>
  defaultValues?: TemplateForm
  isEditing?: boolean
}

export function TemplateModal({
  modalState,
  defaultValues = EMPTY_TEMPLATE_FORM,
  isEditing = false,
}: TemplateModalProps) {
  const intl = useIntl()
  const { $t } = intl

  const { form: mainForm } = useFormStateContext()
  const { navigate } = useNavigation()
  const { track } = useAnalytics()
  const { start, stop } = useTrackTimer()


  const initialPromotion = useRef<Promotion>(mainForm.getValues())

  const form = useFormState({
    defaultValues: { ...defaultValues },
    resolver: vestResolver(validationSuite(intl)),
  })

  const { save, isLoading } = useTemplateMutation()

  const handleSave = async () => {
    const isValid = await form.trigger()

    if (isValid) {
      const values: TemplateForm = form.getValues()
      const promotion: Promotion = mainForm.getValues()

      if (!isEditing) {
        promotion.restrictions.budget = undefined
        promotion.id = undefined
      }

      const template: TemplatePayload = {
        ...values,
        promotion,
      }

      const eventData: Record<string, unknown> = {
        isEditing,
        ...template,
      }

      if (isEditing) {
        eventData.changes = {
          template: getObjectDiff(values, defaultValues),
          promotion: getObjectDiff(promotion, initialPromotion.current),
        }
      }
      
      start()
      track(Event.TEMPLATE_SAVE_CLICK, eventData)
      save({
        data: template,
        onSuccess: () => {
          stop('Template Save Success', { eventData })
          modalState.hide()
          navigate('/')
        },
        onError: () => {
          stop('Template Save Error', { eventData })
        }
      })
    }
  }

  const handleCancel = () => {
    track(Event.CREATE_TEMPLATE_MODAL_CLOSE_CLICK)
    modalState.hide()
  }

  useEffect(() => {
    if (modalState.open) {
      form.reset({ ...defaultValues })
      track(Event.CREATE_TEMPLATE_MODAL_VIEW, {
        isEditing,
        ...mainForm.getValues(),
      })
    }

  }, [form, modalState.open, defaultValues])

  return (
    <Modal state={modalState}>
      <ModalHeader className={styles.modalHeader}>
        <ModalTitle>
          <FormattedMessage {...messages.title} />
        </ModalTitle>
        <ModalDismiss onClick={handleCancel} />
      </ModalHeader>
      <div className={styles.modalSubtitle}>
        <Text variant="body" tone="secondary">
          <FormattedMessage {...messages.subtitle} />
        </Text>
      </div>
      <BudgetWarning />
      <ModalContent className={styles.modalContent}>
        <TextInput
          name="name"
          state={form}
          label={$t(messages.name)}
          onBlurCapture={() => form.clearErrors('name')}
        />
        <TextArea
          name="description"
          state={form}
          label={$t(messages.description)}
        />
      </ModalContent>
      <ModalFooter>
        <Button size="large" variant="secondary" onClick={handleCancel}>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button size="large" onClick={handleSave} loading={isLoading}>
          <FormattedMessage {...messages.save} />
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export function CreateTemplateButton() {
  const modalState = useModalState()

  const { track } = useAnalytics()

  const handleSaveTemplateClick = () => {
    track(Event.CREATE_TEMPLATE_BUTTON_CLICK)
    modalState.show()
  }

  return (
    <>
      <Button
        onClick={handleSaveTemplateClick}
        size="large"
        icon={<IconTemplate />}
        variant="tertiary"
      >
        <FormattedMessage {...messages.title} />
      </Button>
      <TemplateModal modalState={modalState} />
    </>
  )
}
